"use client";
// beui.dev/components/motion/bouncy-accordion
//
// Bavimail branding (upstream API kept as a superset, export names unchanged):
// - Look: no rounded card per item. Rows are separated by hairline DASHED dividers
//   (`border-hb-card`), header is a Manrope bold question, the indicator is `text-hb-faint`
//   and turns `text-hb-accent` while open, description is muted Manrope prose.
// - Motion: THE BOUNCE IS GONE (DESIGN.md forbids bouncy/elastic motion). Upstream's layout
//   projection springs and the morphing group radii were replaced with a plain ease-out
//   height tween (0 <-> auto) plus a content fade. Reduced motion = instant.
// - A11y: real `<button aria-expanded aria-controls>` with the brand focus-visible ring,
//   content is `role="region"` + `aria-labelledby`, closed content is `inert`.
//
// Bavimail additions (everything else is upstream):
// - `type?: "single" | "multiple"` (default "single"). With "multiple" the
//   `value` / `defaultValue` / `onValueChange` trio speaks `string[]`.
// - `variant?: "plain" | "panel"` (default "plain"). "panel" wraps the list in one dashed
//   inset well with padded rows and a row hover fill, for dashboard lists.
// - `size?: "md" | "sm" | "xs"` (default "md"). md = roomy FAQ rows, sm = compact dashboard
//   rows, xs = a small UPPERCASE mono label disclosure ("Advanced settings").
// - `indicator?: "chevron" | "plus"` (default "chevron"), `align?: "center" | "start"`
//   (default "center"), `dividers?: boolean` (default true, false for size "xs").
// - Item: `title` / `meta` / `content` accept a render function `({ open }) => ReactNode`;
//   new `subtitle`, `meta` (trailing slot before the indicator) and `content` (rich,
//   self-styled body; `description` stays the muted prose body).
// - classNames: new `header`, `subtitle`, `meta`, `body` slots.
// NOTE: `font-hb` / `font-mono-hb` are plain CSS classes, tailwind-merge cannot swap them, so
// do not pass a font class through `classNames.title`; pick a `size` instead.

import { motion, useReducedMotion, type Transition } from "motion/react";
import { ChevronDown, Plus } from "lucide-react";
import { useCallback, useId, useState, type ReactNode } from "react";
import { EASE_OUT } from "@/lib/ease";
import { cn } from "@/lib/utils";

export type BouncyAccordionItemState = { open: boolean };

type BouncyAccordionSlot = ReactNode | ((state: BouncyAccordionItemState) => ReactNode);

export type BouncyAccordionItem = {
  id: string;
  /** Header label. Rich nodes are fine; keep them phrasing content (spans), it sits in a button. */
  title: BouncyAccordionSlot;
  /** Small mono line under the title. */
  subtitle?: ReactNode;
  /** Muted prose body (upstream). Styled by the component. */
  description?: ReactNode;
  /** Rich body rendered after `description`. Only padded by the component, styles itself. */
  content?: BouncyAccordionSlot;
  /** Leading slot (icon, avatar). */
  icon?: ReactNode;
  /** Trailing slot, rendered before the indicator (counters, dates, status icons). */
  meta?: BouncyAccordionSlot;
  disabled?: boolean;
};

export type BouncyAccordionClassNames = {
  root?: string;
  item?: string;
  trigger?: string;
  icon?: string;
  header?: string;
  title?: string;
  subtitle?: string;
  meta?: string;
  chevron?: string;
  content?: string;
  description?: string;
  body?: string;
};

export type BouncyAccordionVariant = "plain" | "panel";
export type BouncyAccordionSize = "md" | "sm" | "xs";

type BouncyAccordionBaseProps = {
  items: BouncyAccordionItem[];
  /** single: the open item can be closed again. multiple: the last open item can be closed. */
  collapsible?: boolean;
  variant?: BouncyAccordionVariant;
  size?: BouncyAccordionSize;
  indicator?: "chevron" | "plus";
  align?: "center" | "start";
  dividers?: boolean;
  className?: string;
  classNames?: BouncyAccordionClassNames;
};

export type BouncyAccordionSingleProps = BouncyAccordionBaseProps & {
  type?: "single";
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
};

export type BouncyAccordionMultipleProps = BouncyAccordionBaseProps & {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
};

export type BouncyAccordionProps = BouncyAccordionSingleProps | BouncyAccordionMultipleProps;

// Quiet ease-out tweens: a tween cannot overshoot, so rows below never drift or rebound.
const HEIGHT_OPEN_TRANSITION: Transition = { duration: 0.24, ease: EASE_OUT };
const HEIGHT_CLOSE_TRANSITION: Transition = { duration: 0.2, ease: EASE_OUT };
const FADE_OPEN_TRANSITION: Transition = { duration: 0.2, ease: EASE_OUT, delay: 0.04 };
const FADE_CLOSE_TRANSITION: Transition = { duration: 0.14, ease: EASE_OUT };
const INDICATOR_TRANSITION: Transition = { duration: 0.2, ease: EASE_OUT };
const INSTANT: Transition = { duration: 0 };

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-hb-ring focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-hb-page";

const SIZE_CLASSES: Record<
  BouncyAccordionSize,
  { trigger: string; title: string; indicator: string; body: string; description: string }
> = {
  md: {
    trigger: "gap-4 py-5",
    title: "font-hb text-sm font-bold leading-snug text-hb-heading md:text-[15px]",
    indicator: "h-4 w-4",
    body: "pb-5",
    description: "pr-8 text-sm",
  },
  sm: {
    trigger: "gap-3 py-3",
    title: "font-hb text-sm font-bold leading-snug text-hb-heading",
    indicator: "h-3.5 w-3.5",
    body: "pb-3",
    description: "text-[13px]",
  },
  xs: {
    trigger: "gap-2 py-2",
    title: "font-mono-hb text-[10px] uppercase tracking-[0.15em] text-hb-muted",
    indicator: "h-3 w-3",
    body: "pb-2",
    description: "text-[13px]",
  },
};

function toOpenIds(value: string | string[] | null | undefined): string[] {
  if (value == null) return [];
  return typeof value === "string" ? [value] : value;
}

function renderSlot(slot: BouncyAccordionSlot, state: BouncyAccordionItemState): ReactNode {
  return typeof slot === "function" ? slot(state) : slot;
}

function BouncyAccordionRow({
  item,
  open,
  contentId,
  triggerId,
  reduce,
  variant,
  size,
  indicator,
  align,
  dividers,
  classNames,
  onToggle,
}: {
  item: BouncyAccordionItem;
  open: boolean;
  contentId: string;
  triggerId: string;
  reduce: boolean | null;
  variant: BouncyAccordionVariant;
  size: BouncyAccordionSize;
  indicator: "chevron" | "plus";
  align: "center" | "start";
  dividers: boolean;
  classNames?: BouncyAccordionClassNames;
  onToggle: () => void;
}) {
  const sizeClasses = SIZE_CLASSES[size];
  const panel = variant === "panel";
  const state: BouncyAccordionItemState = { open };
  const meta = renderSlot(item.meta, state);
  const content = renderSlot(item.content, state);
  const hasDescription = item.description != null && item.description !== false;
  const hasContent = content != null && content !== false;
  const IndicatorIcon = indicator === "plus" ? Plus : ChevronDown;

  return (
    <div
      data-state={open ? "open" : "closed"}
      className={cn(
        "text-hb-text",
        dividers && "border-b border-dashed border-hb-card",
        dividers && panel && "last:border-b-0",
        item.disabled && "cursor-not-allowed opacity-50",
        classNames?.item,
      )}
    >
      <button
        id={triggerId}
        type="button"
        disabled={item.disabled}
        aria-expanded={open}
        aria-controls={contentId}
        data-state={open ? "open" : "closed"}
        onClick={onToggle}
        className={cn(
          "group relative flex w-full rounded-sm text-left transition-colors",
          "duration-[var(--motion-duration-fast)] focus-visible:z-10",
          "disabled:pointer-events-none",
          align === "start" ? "items-start" : "items-center",
          sizeClasses.trigger,
          panel && "px-4 hover:bg-hb-surface/40",
          FOCUS_RING,
          classNames?.trigger,
        )}
      >
        {item.icon ? (
          <span className={cn("grid shrink-0 place-items-center text-hb-muted", classNames?.icon)}>
            {item.icon}
          </span>
        ) : null}
        <span className={cn("block min-w-0 flex-1", classNames?.header)}>
          <span
            className={cn(
              "block transition-colors duration-[var(--motion-duration-fast)]",
              sizeClasses.title,
              !panel && size === "xs" && "group-hover:text-hb-heading",
              !panel && size !== "xs" && "group-hover:text-hb-accent",
              classNames?.title,
            )}
          >
            {renderSlot(item.title, state)}
          </span>
          {item.subtitle != null && item.subtitle !== false ? (
            <span
              className={cn(
                "mt-0.5 block font-mono-hb text-[10px] leading-relaxed text-hb-muted",
                classNames?.subtitle,
              )}
            >
              {item.subtitle}
            </span>
          ) : null}
        </span>
        {meta != null && meta !== false ? (
          <span
            className={cn(
              "flex shrink-0 items-center gap-2 font-mono-hb text-[10px] text-hb-muted",
              classNames?.meta,
            )}
          >
            {meta}
          </span>
        ) : null}
        <motion.span
          aria-hidden
          initial={false}
          animate={{ rotate: open ? (indicator === "plus" ? 45 : 180) : 0 }}
          transition={reduce ? INSTANT : INDICATOR_TRANSITION}
          className={cn(
            "grid shrink-0 place-items-center transition-colors",
            "duration-[var(--motion-duration-fast)]",
            align === "start" && "mt-0.5",
            open ? "text-hb-accent" : "text-hb-faint",
            classNames?.chevron,
          )}
        >
          <IndicatorIcon className={sizeClasses.indicator} />
        </motion.span>
      </button>

      <motion.div
        id={contentId}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!open}
        inert={!open}
        initial={false}
        animate={{ height: open ? "auto" : 0 }}
        transition={reduce ? INSTANT : open ? HEIGHT_OPEN_TRANSITION : HEIGHT_CLOSE_TRANSITION}
        className={cn("overflow-hidden", classNames?.content)}
      >
        <motion.div
          initial={false}
          animate={{ opacity: open ? 1 : 0 }}
          transition={reduce ? INSTANT : open ? FADE_OPEN_TRANSITION : FADE_CLOSE_TRANSITION}
          className={cn(panel && "px-4", (hasDescription || hasContent) && sizeClasses.body)}
        >
          {hasDescription ? (
            <div
              className={cn(
                "font-hb leading-relaxed text-hb-muted",
                sizeClasses.description,
                hasContent && "pb-3",
                classNames?.description,
              )}
            >
              {item.description}
            </div>
          ) : null}
          {hasContent ? <div className={classNames?.body}>{content}</div> : null}
        </motion.div>
      </motion.div>
    </div>
  );
}

export function BouncyAccordion(props: BouncyAccordionProps) {
  const {
    items,
    collapsible = true,
    variant = "plain",
    size = "md",
    indicator = "chevron",
    align = "center",
    className,
    classNames,
  } = props;
  const dividers = props.dividers ?? size !== "xs";
  const reduce = useReducedMotion();
  const baseId = useId();

  const [internalIds, setInternalIds] = useState<string[]>(() => toOpenIds(props.defaultValue));
  const isControlled = props.value !== undefined;
  const openIds = isControlled ? toOpenIds(props.value) : internalIds;

  const multiple = props.type === "multiple";
  const onValueChange = props.onValueChange;

  const toggleItem = useCallback(
    (id: string) => {
      const isOpen = openIds.includes(id);
      let next: string[];

      if (multiple) {
        if (isOpen && !collapsible && openIds.length === 1) return;
        next = isOpen ? openIds.filter((openId) => openId !== id) : [...openIds, id];
      } else {
        if (isOpen && !collapsible) return;
        next = isOpen ? [] : [id];
      }

      if (!isControlled) {
        setInternalIds(next);
      }

      if (multiple) {
        (onValueChange as BouncyAccordionMultipleProps["onValueChange"])?.(next);
      } else {
        (onValueChange as BouncyAccordionSingleProps["onValueChange"])?.(next[0] ?? null);
      }
    },
    [collapsible, isControlled, multiple, onValueChange, openIds],
  );

  return (
    <div
      className={cn(
        "w-full",
        variant === "panel"
          ? "rounded-sm border border-dashed border-hb-card bg-hb-inset"
          : dividers && "border-t border-dashed border-hb-card",
        className,
        classNames?.root,
      )}
    >
      {items.map((item, index) => (
        <BouncyAccordionRow
          key={item.id}
          item={item}
          open={openIds.includes(item.id)}
          contentId={`${baseId}-${index}-content`}
          triggerId={`${baseId}-${index}-trigger`}
          reduce={reduce}
          variant={variant}
          size={size}
          indicator={indicator}
          align={align}
          dividers={dividers}
          classNames={classNames}
          onToggle={() => toggleItem(item.id)}
        />
      ))}
    </div>
  );
}
